import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Dialog, Input, Button } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { navigate } from '@/app/router';
import type { Folder } from '@/contracts/funnel';
import type { TemplateSummary } from '@/contracts/templates';

export interface UseTemplateDialogProps {
  template: TemplateSummary | null;
  onClose: () => void;
}

/** SPEC-010 §29/§35/§36/AC-033/AC-034 — name the new funnel, optionally place it in an owned folder. */
export function UseTemplateDialog({ template, onClose }: UseTemplateDialogProps) {
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState<string>('');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!template) return;
    setName(template.name);
    setFolderId('');
    setError(null);
    dashboardApi.listFolders().then((res) => setFolders(res.items));
  }, [template]);

  if (!template) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template) return;
    setSubmitting(true);
    setError(null);
    try {
      const { funnel } = await dashboardApi.instantiateTemplate(template.type, template.id, {
        name: name.trim() || undefined,
        folderId: folderId || null,
      });
      onClose();
      navigate(`/app/funnels/${funnel.id}`);
    } catch {
      setError('Não foi possível criar o funil a partir deste modelo. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={Boolean(template)} onClose={onClose} title={`Usar "${template.name}"`}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="Nome do funil"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
          disabled={submitting}
        />
        {folders.length > 0 ? (
          <label className="flex flex-col gap-1 text-sm text-text-secondary">
            Pasta (opcional)
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              disabled={submitting}
              className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text-primary"
            >
              <option value="">Sem pasta</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting || name.trim().length === 0}>
            {submitting ? 'Criando…' : 'Criar funil'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
